import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Service for handling HTTP routing and requests.
 * Provides methods for GET, POST, PUT, and DELETE operations with URL parameter replacement.
 */
@Injectable({
  providedIn: 'root',
})
export class HttpRoutingService {
  /**
   * HttpClient instance for making HTTP requests.
   */
  private httpClient = inject(HttpClient);

  /**
   * Environment configuration containing API URLs.
   */
  private readonly environment = environment;
  /**
   * Method which is used to replace URL parameters in a string.
   * @param url The URL string containing placeholders like {key}.
   * @param obj Data object containing values to replace placeholders.
   * @returns Replaced URL string.
   */
  replaceUrlParam(url: any, obj: any) {
    for (const key in obj) {
      const replaceText = '{' + key + '}';
      url = url.replace(replaceText, obj[key]);
    }
    return url;
  }

  /**
   * Sends a GET API request to the server.
   * @param url API endpoint path.
   * @param queryParams Optional query parameters for the request.
   * @returns An Observable of the response.
   */
  getMethod(url: string, queryParams?: any, headers?: HttpHeaders | Record<string, string>): Observable<any> {
    if (!queryParams) {
      queryParams = {};
    }
    const apiUrl = this.environment.apiBaseUrl + url;
    return this.httpClient.get(apiUrl, {
      params: queryParams,
      headers,
    });
  }

  /**
   * Sends a POST API request to the server.
   * @param url API endpoint path.
   * @param data Request body data.
   * @param queryParams Optional query parameters for the request.
   * @returns An Observable of the response.
   */
  postMethod(url: string, data: any, queryParams?: any, headers?: HttpHeaders | Record<string, string>): Observable<any> {
    const apiUrl = this.environment?.apiBaseUrl + url;
    return this.httpClient.post(apiUrl, data, {
      params: queryParams,
      headers,
    });
  }

  /**
   * Sends a PUT API request to the server.
   * @param url API endpoint path.
   * @param data Request body data.
   * @param queryParams Optional query parameters for the request.
   * @returns An Observable of the response.
   */
  putMethod(url: string, data: any, queryParams?: any, headers?: HttpHeaders | Record<string, string>): Observable<any> {
    const apiUrl = this.environment.apiBaseUrl + url;
    return this.httpClient.put(apiUrl, data, {
      params: queryParams,
      headers,
    });
  }

  /**
   * Sends a DELETE API request to the server.
   * @param url API endpoint path.
   * @param queryParams Optional query parameters for the request.
   * @returns An Observable of the response.
   */
  deleteMethod(url: string, queryParams?: any, headers?: HttpHeaders | Record<string, string>): Observable<any> {
    const apiUrl = this.environment.apiBaseUrl + url;
    return this.httpClient.delete(apiUrl, { params: queryParams, headers });
  }

}